/**
 * 13L Products Carousel
 * Clean implementation with proper section isolation
 */
(function () {
  "use strict";

  function initSection(section) {
    if (section.hasAttribute("data-nbc-initialized")) return;
    section.setAttribute("data-nbc-initialized", "true");
    new CarouselSection(section);
  }

  function initAll(root) {
    root
      .querySelectorAll('[data-section-type="tl-newbest-carousel"]')
      .forEach(initSection);
  }

  initAll(document);

  document.addEventListener("shopify:section:load", function (event) {
    initAll(event.target);
  });

  document.addEventListener("shopify:section:unload", function (event) {
    event.target
      .querySelectorAll('[data-section-type="tl-newbest-carousel"]')
      .forEach(function (section) {
        if (typeof section.__nbcCleanup === "function") {
          section.__nbcCleanup();
        }
      });
  });

  /**
   * CarouselSection - Manages a single carousel section instance
   */
  function CarouselSection(sectionElement) {
    const section = sectionElement;
    let resizeScheduled = false;
    let resizeObserverInstance = null;
    let yotpoObserverInstance = null;

    /**
     * Equalize product card heights in active panel
     */
    function equalizeCards() {
      try {
        const activePanel = section.querySelector(".tl-nbc__panel.is-active");
        if (!activePanel) return;

        const cards = activePanel.querySelectorAll(".tl-nbc__product-inner");
        if (cards.length === 0) return;

        cards.forEach(function (card) {
          card.style.height = "auto";
        });

        let maxHeight = 0;
        cards.forEach(function (card) {
          const height = card.offsetHeight;
          if (height > maxHeight) maxHeight = height;
        });

        if (maxHeight > 0) {
          cards.forEach(function (card) {
            card.style.height = maxHeight + "px";
            card.classList.add("tl-nbc--eq-ready");
          });
        }
      } catch (e) {
        // swallow — equalizing is a visual enhancement, not critical path
      }
    }

    /**
     * Debounced equalize cards
     */
    function scheduleEqualize() {
      if (resizeScheduled) return;
      resizeScheduled = true;

      requestAnimationFrame(function () {
        resizeScheduled = false;
        equalizeCards();
      });
    }

    /**
     * Setup resize observer for card content changes
     */
    function setupResizeObserver() {
      if (!window.ResizeObserver) return;

      resizeObserverInstance = new ResizeObserver(scheduleEqualize);

      const activePanel = section.querySelector(".tl-nbc__panel.is-active");
      if (activePanel) {
        activePanel
          .querySelectorAll(".tl-nbc__product-inner")
          .forEach(function (card) {
            resizeObserverInstance.observe(card);
          });
      }
    }

    /**
     * Setup tab switching
     */
    function setupTabs() {
      const tabs = section.querySelectorAll(".tl-nbc__tab");
      const panels = section.querySelectorAll(".tl-nbc__panel");

      if (tabs.length === 0 || panels.length === 0) return;

      tabs.forEach(function (tab) {
        tab.addEventListener("click", function () {
          const targetId = tab.getAttribute("data-target");
          const targetPanel = section.querySelector(
            '[id^="panel-' + targetId + '"]'
          );

          if (!targetPanel) return;

          // Update tabs
          tabs.forEach(function (t) {
            const isActive = t === tab;
            t.classList.toggle("is-active", isActive);
            t.setAttribute("aria-selected", isActive);
            if (t.parentElement) {
              t.parentElement.classList.toggle("is-active", isActive);
            }
          });

          // Update panels
          panels.forEach(function (panel) {
            const isActive = panel === targetPanel;
            panel.classList.toggle("is-active", isActive);
            panel.setAttribute("aria-hidden", !isActive);
            panel.toggleAttribute("inert", !isActive);
          });

          // Update Shop All link
          const shopAllLink = section.querySelector(
            "[data-nbc-global-shopall]"
          );
          if (shopAllLink) {
            const collectionUrl = targetPanel.getAttribute(
              "data-collection-url"
            );
            if (collectionUrl) {
              shopAllLink.href = collectionUrl;
              shopAllLink.classList.remove("is-disabled");
            } else {
              shopAllLink.removeAttribute("href");
              shopAllLink.classList.add("is-disabled");
            }
          }

          // Equalize cards in new panel
          setTimeout(function () {
            equalizeCards();
          }, 50);
        });
      });
    }

    /**
     * Setup color swatch carousels (native scroll, no external library)
     */
    function setupSwatchCarousels() {
      const swatchContainers = section.querySelectorAll(
        ".tl-nbc__swatches--carousel"
      );

      swatchContainers.forEach(function (swatchContainer) {
        if (swatchContainer.hasAttribute("data-scroll-initialized")) {
          return;
        }

        const track = swatchContainer.querySelector(".tl-nbc__swatches-track");
        const prevBtn = swatchContainer.querySelector(
          ".tl-nbc__swatch-nav--prev"
        );
        const nextBtn = swatchContainer.querySelector(
          ".tl-nbc__swatch-nav--next"
        );

        if (!track || !prevBtn || !nextBtn) return;

        function updateNavState() {
          prevBtn.disabled = track.scrollLeft <= 0;
          nextBtn.disabled =
            track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
        }

        prevBtn.addEventListener("click", function () {
          track.scrollBy({ left: -track.clientWidth, behavior: "smooth" });
        });

        nextBtn.addEventListener("click", function () {
          track.scrollBy({ left: track.clientWidth, behavior: "smooth" });
        });

        track.addEventListener("scroll", updateNavState);
        updateNavState();

        swatchContainer.setAttribute("data-scroll-initialized", "true");
      });
    }

    /**
     * Setup swatch click handlers
     */
    function setupSwatchClicks() {
      section
        .querySelectorAll(".tl-nbc__product-inner")
        .forEach(function (productCard) {
          const productId = productCard.getAttribute("data-product-id");
          const productHandle = productCard.getAttribute("data-product-handle");

          if (!productId) return;

          const swatches = productCard.querySelectorAll(".tl-nbc__swatch");

          swatches.forEach(function (swatch) {
            function selectSwatch() {
              const colorValue = swatch.getAttribute("data-color");

              swatches.forEach(function (s) {
                s.classList.remove("is-active");
                s.setAttribute("aria-pressed", "false");
              });

              swatch.classList.add("is-active");
              swatch.setAttribute("aria-pressed", "true");

              fetch("/products/" + productHandle + ".js")
                .then(function (response) {
                  return response.json();
                })
                .then(function (productData) {
                  const matchingVariant = productData.variants.find(function (
                    variant
                  ) {
                    return variant.options.some(function (option) {
                      return option.toLowerCase() === colorValue.toLowerCase();
                    });
                  });

                  if (matchingVariant) {
                    const imageLink = productCard.querySelector(
                      ".tl-nbc__image-link"
                    );
                    const image = productCard.querySelector(".tl-nbc__image");

                    if (matchingVariant.featured_image && image) {
                      image.src = matchingVariant.featured_image.src.replace(
                        /\.(jpg|jpeg|png|gif|webp)/i,
                        "_600x.$1"
                      );
                      image.alt =
                        matchingVariant.featured_image.alt || productData.title;
                    }

                    const priceEl = productCard.querySelector(".tl-nbc__price");
                    if (priceEl) {
                      let priceHTML = "";
                      if (
                        matchingVariant.compare_at_price &&
                        matchingVariant.compare_at_price > matchingVariant.price
                      ) {
                        priceHTML =
                          '<span class="tl-nbc__price--sale">' +
                          formatMoney(matchingVariant.price) +
                          "</span>";
                        priceHTML +=
                          '<s class="tl-nbc__price--compare">' +
                          formatMoney(matchingVariant.compare_at_price) +
                          "</s>";
                      } else {
                        priceHTML =
                          "<span>" +
                          formatMoney(matchingVariant.price) +
                          "</span>";
                      }
                      priceEl.innerHTML = priceHTML;
                    }

                    const addBtn = productCard.querySelector(".tl-nbc__add");
                    if (addBtn) {
                      addBtn.setAttribute(
                        "data-variant-id",
                        matchingVariant.id
                      );
                      addBtn.disabled = !matchingVariant.available;

                      if (matchingVariant.available) {
                        addBtn.textContent =
                          addBtn.getAttribute("data-atc-label") || "ADD TO BAG";
                      } else {
                        addBtn.textContent =
                          addBtn.getAttribute("data-soldout-label") ||
                          "SOLD OUT";
                      }
                    }
                  }
                })
                .catch(function () {
                  // swallow — swatch selection stays on current image/price if the fetch fails
                });
            }

            swatch.addEventListener("click", function (e) {
              e.preventDefault();
              selectSwatch();
            });

            swatch.addEventListener("keydown", function (e) {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                selectSwatch();
              }
            });
          });

          if (
            swatches.length > 0 &&
            !productCard.querySelector(".tl-nbc__swatch.is-active")
          ) {
            swatches[0].classList.add("is-active");
            swatches[0].setAttribute("aria-pressed", "true");
          }
        });
    }

    /**
     * Format money helper
     */
    function formatMoney(cents) {
      const dollars = (cents / 100).toFixed(2);
      return "$" + dollars;
    }

    /**
     * Update header cart count
     */
    function updateCartCount() {
      fetch("/cart.js")
        .then(function (response) {
          return response.json();
        })
        .then(function (cart) {
          const itemCount = cart.item_count;

          document.dispatchEvent(
            new CustomEvent("cart:count-changed", {
              bubbles: true,
              detail: { count: itemCount },
            })
          );
        })
        .catch(function () {
          // swallow — header cart count simply won't refresh this cycle
        });
    }

    /**
     * Setup add to cart buttons
     */
    function setupAddToCart() {
      section.querySelectorAll(".tl-nbc__add").forEach(function (btn) {
        if (btn.hasAttribute("data-atc-initialized")) {
          return;
        }
        btn.setAttribute("data-atc-initialized", "true");

        btn.addEventListener("click", function () {
          const variantId = btn.getAttribute("data-variant-id");

          if (!variantId || btn.disabled) {
            return;
          }

          if (btn.classList.contains("is-adding")) {
            return;
          }

          const originalLabel =
            btn.getAttribute("data-atc-label") || "ADD TO BAG";
          const addingLabel = "ADDING TO BAG...";
          const addedLabel = "ADDED ✓";

          btn.classList.add("is-adding");
          btn.disabled = true;
          btn.textContent = addingLabel;

          fetch("/cart/add.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              id: variantId,
              quantity: 1,
            }),
          })
            .then(function (response) {
              if (!response.ok) {
                throw new Error("Add to cart failed");
              }
              return response.json();
            })
            .then(function (data) {
              btn.textContent = addedLabel;
              btn.classList.remove("is-adding");
              btn.classList.add("is-added");

              updateCartCount();

              document.dispatchEvent(
                new CustomEvent("cart:updated", { detail: data })
              );

              document.documentElement.dispatchEvent(
                new CustomEvent("cart:refresh", { bubbles: true })
              );

              setTimeout(function () {
                btn.textContent = originalLabel;
                btn.classList.remove("is-added");
                btn.disabled = false;
              }, 2000);
            })
            .catch(function () {
              btn.textContent = originalLabel;
              btn.classList.remove("is-adding");
              btn.disabled = false;
            });
        });
      });
    }

    /**
     * Watch for Yotpo reviews loading
     */
    function watchYotpo() {
      if (!section.querySelector(".yotpo-widget-instance")) return;

      yotpoObserverInstance = new MutationObserver(function (mutations) {
        let hasNewContent = false;

        mutations.forEach(function (mutation) {
          if (mutation.addedNodes.length > 0) {
            hasNewContent = true;
          }
        });

        if (hasNewContent) {
          setTimeout(function () {
            equalizeCards();
          }, 300);
        }
      });

      section
        .querySelectorAll(".yotpo-widget-instance")
        .forEach(function (widget) {
          yotpoObserverInstance.observe(widget, {
            childList: true,
            subtree: true,
          });
        });
    }

    /**
     * Cleanup observers/listeners on section unload (Theme Editor)
     */
    function cleanup() {
      window.removeEventListener("resize", scheduleEqualize);
      if (resizeObserverInstance) resizeObserverInstance.disconnect();
      if (yotpoObserverInstance) yotpoObserverInstance.disconnect();
    }

    /**
     * Initialize section
     */
    function init() {
      setupTabs();
      setupAddToCart();
      setupResizeObserver();
      watchYotpo();

      setTimeout(function () {
        setupSwatchCarousels();
        setupSwatchClicks();
      }, 200);

      window.addEventListener("resize", scheduleEqualize);

      setTimeout(equalizeCards, 200);
    }

    init();
    section.__nbcCleanup = cleanup;
  }
})();
